// frontend/src/services/document/documentService.js
import api from '../api';
import errorService from '../error/errorService';

/**
 * Document Service - Handles all document-related operations
 */
class DocumentService {
  /**
   * Get a document by ID
   * @param {string} documentId - The ID of the document
   * @returns {Promise} - Promise that resolves with the document
   */
  async getDocumentById(documentId) {
    try {
      const response = await api.get(`/documents/${documentId}`);
      return response.data;
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to fetch document');
    }
  }

  /**
   * Get all documents for a project
   * @param {string} projectId - The ID of the project
   * @returns {Promise} - Promise that resolves with the documents
   */
  async getDocumentsByProject(projectId) {
    try {
      const response = await api.get(`/projects/${projectId}/documents`);
      return response.data;
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to fetch documents');
    }
  }

  /**
   * Create a new document
   * @param {string} projectId - The ID of the project
   * @param {FormData} formData - Form data with title and file
   * @returns {Promise} - Promise that resolves with the new document
   */
  async createDocument(projectId, formData) {
    try {
      const response = await api.post(`/documents?project_id=${projectId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to create document');
    }
  }

  /**
   * Update document title
   * @param {string} documentId - The ID of the document
   * @param {string} title - The new title
   * @returns {Promise} - Promise that resolves with the updated document
   */
  async updateTitle(documentId, title) {
    try {
      const response = await api.put(`/documents/${documentId}/title`, { title });
      return response.data;
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to update document title');
    }
  }

  /**
   * Delete a document
   * @param {string} documentId - The ID of the document
   * @returns {Promise} - Promise that resolves when document is deleted
   */
  async deleteDocument(documentId) {
    try {
      const response = await api.delete(`/documents/${documentId}`);
      return response.data;
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to delete document');
    }
  }

  /**
   * Get document text from URL
   * @param {string} url - The URL of the document
   * @returns {Promise<string>} - Promise that resolves with the document text
   */
  async getDocumentText(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch document text: ${response.status} ${response.statusText}`);
      }
      return await response.text();
    } catch (error) {
      console.error("Error fetching document text:", error);
      throw error;
    }
  }
}

export default new DocumentService();